import React, { useEffect, useReducer, useState } from 'react';
import { Button, Card, GeofenceLayer, Map, RotatedImageOverlay, Table } from '../../Common';
import CongestionFilterGroup from './Component/CongestionFilterGroup';
import congestionStatusReducer, {
    initialState,
    setEndDate,
    setSelectedCategory,
    setSelectedFloor,
    setSelectedFloorInfo,
    setStartDate,
} from './congestionStatusReducer';
import { useSelector } from 'react-redux';
import { useAsync, useColumns, useTranslation } from '@hooks';
import { fetchGeofenceList } from '@api/common';
import * as column from '../../MainPages/util/grid/column';
import Control from 'react-leaflet-control';
import { fetchCongestionStatusApi } from '@api/statistics';
import moment from 'moment';
import SelectedCongestionSummary from './Component/SelectedCongestionSummary';
import AllCongestionSummary from './Component/AllCongestionSummary';
import FloorTreeSelect from '../Components/FloorTreeSelect';
import WidgetCard from '../Components/WidgetCard';

export const CongestionStatusContext = React.createContext();
export const CongestionStatusDispatchContext = React.createContext();

const defaultCongestionInfo = {
    totalAvgStayEntryTime: 0,
    totalAvgStayUniqueTime: 0,
    totalPermittedEntryCount: 0,
    totalPermittedUniqueCount: 0,
    totalRestrictedEntryCount: 0,
    totalRestrictedUniqueCount: 0,
};

const defaultSelectedCongestionInfo = {
    avgStayEntryTime: 0,
    avgStayUniqueTime: 0,
    entryCount: 0,
    uniqueCount: 0,
};

const CongestionStatus = ({ children, widgetInfo, ...restProps }) => {
    const t = useTranslation('CongestionStatus');
    const [showSearchArea, setShowSearchArea] = useState(false);
    const [selectedGeofence, setSelectedGeofence] = useState({});
    const [selectedGeofenceCongestionInfo, setSelectedGeofenceCongestionInfo] = useState({ ...defaultCongestionInfo });
    const [state, dispatch] = useReducer(congestionStatusReducer, initialState);
    const { floorList } = useSelector(state => state.FloorInfo);
    const { selectedCategory, selectedFloor, selectedFloorInfo, startDate, endDate } = state;
    const [geofenceInfo, setGeofenceInfo] = useState([]);
    const [filterParam, setFilterParam] = useState({});
    const [congestionInfo, setCongestionInfo] = useState([]);
    const { promise: getGeofenceList } = useAsync({
        promise: fetchGeofenceList,
        resolve: res => {
            if (res) {
                setGeofenceInfo(
                    res.rows.map(geofence => {
                        return {
                            ...geofence,
                            bounds: [geofence.latLngList.map(({ lat, lng }) => [lat, lng])],
                        };
                    }),
                );
            }
        },
    });

    const { promise: getCongestionStatus } = useAsync({
        promise: fetchCongestionStatusApi,
        resolve: res => {
            setCongestionInfo(res);
        },
    });

    const columns = useColumns([
        column.geofenceName({ width: 50 }),
        column.countOfEntries({ width: 50 }),
        column.countOfUnique({ width: 50 }),
        column.averageStayOfEntry({ width: 50 }),
        column.averageStayOfUnique({ width: 50 }),
    ]);

    const handleSearchAreaClick = () => {
        setShowSearchArea(!showSearchArea);
    };
    const handleRefreshClick = () => {
        dispatch(setSelectedCategory(''));
        dispatch(setSelectedFloor(floorList[0] ? floorList[0].floorId : ''));
        dispatch(setStartDate(moment().startOf('day').valueOf()));
        dispatch(setEndDate(moment().endOf('day').valueOf()));
        dispatch(setSelectedFloorInfo(floorList[0] ? floorList[0] : {}));
    };

    const handleSearchClick = e => {
        if (selectedFloor && startDate && endDate) {
            getCongestionStatus({
                ...filterParam,
                startDate: startDate ? moment(startDate).unix() : null,
                endDate: endDate ? moment(endDate).unix() : null,
            });
        }
        setShowSearchArea(false);
    };

    const handleFloorChange = selects => {
        if (selects[0]) {
            dispatch(setSelectedFloor(selects[0].floorId));
            dispatch(setSelectedFloorInfo(selects[0]));
        }
    };

    useEffect(() => {
        if (floorList[0]) {
            dispatch(setSelectedFloorInfo(floorList[0]));
            getGeofenceList({ floor: floorList[0].floorId });
        }
    }, []);

    useEffect(() => {
        if (selectedFloorInfo.floorName) {
            getCongestionStatus({
                floorId: selectedFloorInfo.floorId,
                startDate: startDate ? moment(startDate).unix() : null,
                endDate: endDate ? moment(endDate).unix() : null,
            });
        }
    }, [selectedFloorInfo]);

    useEffect(() => {
        if (selectedFloor) {
            getCongestionStatus({
                ...filterParam,
                startDate: startDate ? moment(startDate).unix() : null,
                endDate: endDate ? moment(endDate).unix() : null,
            });
            getGeofenceList({ floor: selectedFloor });
            setSelectedGeofenceCongestionInfo({});
        }
    }, [filterParam]);

    useEffect(() => {
        const paramInfo = {};
        if (selectedCategory) {
            paramInfo.categoryCode = selectedCategory;
        }
        if (selectedFloor) {
            paramInfo.floorId = selectedFloor;
        }
        setFilterParam(paramInfo);
    }, [selectedCategory, selectedFloor]);

    useEffect(() => {
        let foundOne = {};

        if (selectedGeofence.fcNum) {
            foundOne = congestionInfo.geofenceList.find(geofence => geofence.fcNum === selectedGeofence.fcNum);
            if (foundOne) {
                setSelectedGeofenceCongestionInfo(foundOne);
            } else {
                setSelectedGeofenceCongestionInfo({ fcNum: selectedGeofence.fcNum, ...defaultSelectedCongestionInfo });
            }
        } else {
            setSelectedGeofenceCongestionInfo({});
        }
    }, [selectedGeofence]);

    const tableHeight = !congestionInfo.geofenceList?.length
        ? '15rem'
        : (31 + congestionInfo.geofenceList.length * 36.4).toString() + 'px';

    return (
        <CongestionStatusDispatchContext.Provider value={dispatch}>
            <CongestionStatusContext.Provider value={state}>
                <WidgetCard
                    widgetInfo={widgetInfo}
                    {...restProps}
                    headerAction={
                        <>
                            <Button className={'btn-darkgray'} onClick={handleSearchAreaClick}>
                                {t('Search', 'Button')}
                            </Button>
                            <div className="pnt-border border-h" />
                            <Button
                                className="btn-lightgray btn-icon-only"
                                iconName="refresh"
                                onClick={handleRefreshClick}
                            />
                        </>
                    }
                    searchFilter={showSearchArea && <CongestionFilterGroup handleSearchClick={handleSearchClick} />}
                >
                    <div className={'grid-container w-100 h-100'} style={{ gridTemplateColumns: '1fr 1fr' }}>
                        <div className={'h-100 w-100'}>
                            <Map>
                                {selectedFloorInfo && selectedFloorInfo.floorName && (
                                    <Control position="topleft">
                                        <FloorTreeSelect
                                            key={selectedFloor}
                                            floorList={floorList}
                                            selectedFloorStr={selectedFloor}
                                            mode={'radioSelect'}
                                            handleChange={handleFloorChange}
                                            isMap
                                        />

                                        {/*<div className={'item-container'}>*/}
                                        {/*    <span className={'map-text'}>{selectedFloorInfo.floorName}</span>*/}
                                        {/*</div>*/}
                                    </Control>
                                )}
                                {selectedFloorInfo.imgURL && (
                                    <RotatedImageOverlay
                                        key={selectedFloorInfo.floorId}
                                        url={selectedFloorInfo.imgURL}
                                        deg={selectedFloorInfo.deg}
                                        bounds={selectedFloorInfo.bounds}
                                        onLoad={e => {
                                            const { target: layer } = e;
                                            layer._map.fitBounds(layer.getBounds());
                                        }}
                                    />
                                )}
                                {geofenceInfo.length > 0 && (
                                    <GeofenceLayer
                                        selectedGeofence={selectedGeofence}
                                        geofenceList={geofenceInfo}
                                        handleClick={geofence => {
                                            geofence.fcNum === selectedGeofence.fcNum
                                                ? setSelectedGeofence({})
                                                : setSelectedGeofence(geofence);
                                        }}
                                    />
                                )}
                            </Map>
                        </div>
                        <Card className={'p-0'}>
                            {selectedGeofenceCongestionInfo.fcNum ? (
                                <SelectedCongestionSummary selectedCongestionInfo={selectedGeofenceCongestionInfo} />
                            ) : (
                                <AllCongestionSummary congestionInfo={congestionInfo} />
                            )}

                            <div style={{ marginTop: '11px', height: tableHeight }}>
                                <Table
                                    paging={false}
                                    data={{
                                        rows: congestionInfo.geofenceList ? congestionInfo.geofenceList : [],
                                    }}
                                    textAlign={'left'}
                                    columns={columns}
                                />
                            </div>
                        </Card>
                    </div>

                    {children}
                </WidgetCard>
            </CongestionStatusContext.Provider>
        </CongestionStatusDispatchContext.Provider>
    );
};

export default CongestionStatus;
